/**
 * Generates a GraphQL query to fetch collection titles based on product collection IDs.
 *
 * @param {Object} product - The product object containing collection IDs.
 * @param {Array<number>} product.collection_ids - Array of collection IDs associated with the product.
 * @returns {string} - The GraphQL query string to fetch collection titles.
 */
function queryRebuyProductCollections(product) {
  let collectionIds = product.collection_ids;
  const formattedIds = collectionIds.map(
    id => `gid://shopify/Collection/${id}`
  );

  // GraphQL query
  const query = `
  {
    nodes(ids: ${JSON.stringify(formattedIds)}) {
      ... on Collection {
        id
        title
      }
    }
  }
  `;
  return query;
}
/**
 * Sends a GraphQL query to the Shopify API to fetch collection titles.
 *
 * @param {Object} product - The product object used to generate the query.
 * @param {Array<number>} product.collection_ids - Array of collection IDs associated with the product.
 * @returns {Promise<Array<string>>} - A promise that resolves to an array of collection titles.
 */
async function fetchCollectionTitles(product) {
  const query = queryRebuyProductCollections(product);
  try {
    const response = await fetch('/api/2025-10/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': '5c81c46bcdaaca9cc24f489ed6e3df0b'
      },
      body: JSON.stringify({ query })
    });

    const jsonResponse = await response.json();

    // Check if nodes array is returned and contains collections
    if (jsonResponse.data && jsonResponse.data.nodes) {
      const titles = jsonResponse.data.nodes
        .filter(node => node && node.title)
        .map(node => node.title);

      return titles;
    } else {
      console.error('No collection titles found in response:', jsonResponse);
      return [];
    }
  } catch (error) {
    console.error('Error fetching collection titles:', error);
    return []; // Return an empty array in case of error
  }
}

/**
 * Creates a Klaviyo item object and pushes it to Klaviyo's tracking system.
 *
 * @param {Object} product - The product object to be included in the Klaviyo item.
 * @param {string} product.title - The title of the product.
 * @param {string} product.id - The unique ID of the product.
 * @param {Array<Object>} product.images - Array of image objects for the product.
 * @param {string} product.handle - The handle (slug) of the product.
 * @param {string} product.vendor - The vendor (brand) of the product.
 * @param {Array<Object>} product.variants - Array of variant objects for the product.
 * @param {string} product.variants.price - The price of the product variant.
 * @param {Array<string>} product.tags - Array of tags associated with the product.
 */
async function createAndPushKlaviyoItem(product) {
  const categories = await fetchCollectionTitles(product);

  let klaviyoItem = {
    Name: String(product.title),
    ProductID: product.id,
    Categories: categories,
    ImageURL: `${product.images[0].src}`,
    URL: `https://stellabluecoffee.com/products/${product.handle}`,
    Brand: String(product.vendor),
    Price: product.variants[0].price,
    Tags: product.tags,
    RebuyProduct: true
  };

  if (typeof _learnq === 'undefined') return;
  if (!window.BSSConsent?.isGranted('marketing')) return;
  _learnq.push(['track', 'Added to Cart', klaviyoItem]);
}

/**
 * Event handler for the 'rebuy.add' event that triggers the creation and pushing of a Klaviyo item.
 *
 * @param {Event} event - The event object containing product data.
 */
document.addEventListener('rebuy.add', event => {
  const product = event.detail.product;
  createAndPushKlaviyoItem(product);
});
